// src/components/sourcex/TableGridEditor.jsx
//
// The interactive grid a coordinator builds a Table field on — the Word /
// Google Docs model, applied to a source-document table.
//
// WHY: a table used to be configured as a vertical stack of "column" cards,
// then a separate Rows block, then a third block for starting values. The
// table itself was never on screen while you built it, so its shape had to be
// held in your head, and "add a column between these two" meant adding one at
// the end and clicking Move-up repeatedly.
//
// Here the table IS the editor. You type column headings into the header row,
// type straight into any cell to have it print already filled in, hover a
// boundary to insert a row or column right there, and use the ⌄ on a heading
// for that column's type, unit and choices. Nothing is a one-time upfront
// decision — every dimension and every setting stays editable for as long as
// the question is open.
//
// A table is exactly the rows and columns on screen. There is no separate
// row-label column: a table that wants row names puts them in its first
// column like any other content, which is also what keeps the exported Word
// and HTML tables to the columns the coordinator actually drew.
//
// The one exception is a table built BEFORE this editor, which may carry
// `rowLabels` — a real leading label column in every renderer. Those are shown
// (and stay editable) so opening an old physical-exam template doesn't quietly
// destroy its row names, and there's a control to fold that column away. New
// tables never grow one.
//
// The saved shape is unchanged: this edits the same `{columns, rowLabels,
// rowLabelHeader, minRows, maxRows, prefillRows}` draft QuestionBuilder always
// kept, so the preview, the HTML export and the Word export all keep reading
// exactly what they read before.

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Plus,
  Trash2,
  ChevronDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  MoreVertical,
  Table2,
} from "lucide-react";
import { inputCls, selectCls } from "./QuestionField";
import OptionsEditor from "./OptionsEditor";
import DefaultValueInput from "./DefaultValueInput";
import {
  PALETTE_TYPES,
  FIELD_TYPE_LABELS,
  FIELD_TYPE_HINTS,
} from "./FieldTypePalette";
import { MAX_TABLE_ROWS, MAX_TABLE_COLS } from "./TableSizeModal";

// Table isn't a column type — a column can't itself be a table. PALETTE_TYPES
// (not the raw label map) so a column's type list is offered in the same order
// as the rail and the question builder.
const COLUMN_TYPES = PALETTE_TYPES.filter((t) => t !== "Table");

let colSeq = 0;
/** A fresh local key for a column. Becomes the column's real id on save, which
 * is what keeps prefillRows (keyed by column id) pointing at the right column. */
export const newColumnKey = () =>
  `col_${Date.now().toString(36)}_${(colSeq++).toString(36)}`;

/** A new column, shaped exactly like the ones QuestionBuilder's seed builds. */
export const blankColumn = (n) => ({
  key: newColumnKey(),
  type: "Text",
  label: `Column ${n}`,
  unit: "",
  options: [],
});

/**
 * How many rows this table has. A legacy table with `rowLabels` is as many
 * rows as it has labels; everything else is `minRows`, falling back to the
 * same 4 tablePrintRowCount uses so an older template with no minRows set
 * shows the 4 rows it actually prints (otherwise "add a row" would jump from
 * a visible 4 straight to 1).
 * @param {object} table
 * @returns {number}
 */
export const tableRowCount = (table) => {
  const labels = Array.isArray(table?.rowLabels) ? table.rowLabels : [];
  if (labels.length) return labels.length;
  return Number.isInteger(table?.minRows) && table.minRows > 0 ? table.minRows : 4;
};

/** prefillRows padded out to `n` entries, so a row can be spliced in or out by
 * position without the values sliding onto the wrong rows. A table that has
 * never been typed into carries no prefillRows at all. */
const padRows = (rows, n) =>
  Array.from({ length: n }, (_, i) => (Array.isArray(rows) && rows[i]) || {});

/** A menu anchored under whatever was clicked, in a portal so the grid's own
 * horizontal scroll can't clip it. The full-screen layer behind it is what
 * closes it on an outside click. */
function AnchoredMenu({ rect, onClose, width = 300, children }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    // The panel is positioned from where its heading was when it opened, so a
    // page scroll would leave it floating away from the column it belongs to.
    // A scroll INSIDE the panel fires on the panel, not the window, so this
    // doesn't fight with a long list of choices being scrolled.
    window.addEventListener("scroll", onClose);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onClose);
    };
  }, [onClose]);

  const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
  const top = Math.min(rect.bottom + 6, window.innerHeight - 120);
  const maxHeight = Math.max(160, window.innerHeight - top - 12);

  return createPortal(
    <div
      className="fixed inset-0 z-[90]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{ left, top, width, maxHeight }}
        className="absolute overflow-y-auto rounded-xl border border-white/12 bg-[#0c0826]/97 backdrop-blur-2xl shadow-[0_20px_60px_-18px_rgba(0,0,0,0.85)]"
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

/** One row of a structural menu (insert / move / delete). */
function MenuAction({ icon: Icon, label, onClick, disabled, danger, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title || label}
      className={`w-full inline-flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] text-left transition disabled:opacity-30 disabled:cursor-not-allowed ${
        danger
          ? "text-rose-300 hover:bg-rose-500/15"
          : "text-slate-300 hover:bg-white/10 hover:text-white"
      }`}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}

/** The small "+" that straddles a row/column boundary, the way Docs shows one
 * when you hover the edge of a table. Dim until the grid is hovered. */
function InsertHandle({ onClick, title, className }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className={`absolute z-10 w-4 h-4 rounded-full bg-purple-500/85 text-white flex items-center justify-center shadow opacity-0 group-hover/grid:opacity-40 hover:!opacity-100 focus:!opacity-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50 transition ${className}`}
    >
      <Plus className="w-2.5 h-2.5" />
    </button>
  );
}

/**
 * @param {object} props.table  the table draft — see the file header
 * @param {Function} props.setTable  React setState for that draft (updater form)
 * @param {() => void} props.onRemove  discard the table entirely
 * @param {Function} props.confirm  the shared confirm-dialog hook, for
 *   OptionsEditor's "this choice is used by a branch" check
 */
export default function TableGridEditor({ table, setTable, onRemove, confirm }) {
  const cols = Array.isArray(table.columns) ? table.columns : [];
  const rowLabels = Array.isArray(table.rowLabels) ? table.rowLabels : [];
  // Only a table built before this editor has a separate label column; a new
  // one never does. See the file header.
  const hasRowLabels = rowLabels.length > 0;
  const rowCount = tableRowCount(table);

  // Which heading / row handle has its menu open, with the rect it was opened
  // from. Held as a key (not an index) for a column so an edit that reorders
  // nothing still points at the same column.
  const [menu, setMenu] = useState(null);
  const [focusRow, setFocusRow] = useState(null);
  const rowInputs = useRef({});

  // Focus a row label that was just created (Enter at the end of a row), so
  // typing a list of rows never means reaching for the mouse between them.
  useEffect(() => {
    if (focusRow == null) return;
    rowInputs.current[focusRow]?.focus();
    setFocusRow(null);
  }, [focusRow]);

  // Stable, so AnchoredMenu's key/scroll listeners aren't torn down and
  // re-registered on every keystroke typed into the menu.
  const closeMenu = useCallback(() => setMenu(null), []);
  const openMenu = (next, ev) =>
    setMenu({ ...next, rect: ev.currentTarget.getBoundingClientRect() });

  // ---- columns -----------------------------------------------------------
  const updateColumn = (key, patch) =>
    setTable((t) => ({
      ...t,
      columns: t.columns.map((c) => (c.key === key ? { ...c, ...patch } : c)),
    }));

  const insertColumn = (at) =>
    setTable((t) => {
      if (t.columns.length >= MAX_TABLE_COLS) return t;
      const next = [...t.columns];
      next.splice(at, 0, blankColumn(t.columns.length + 1));
      return { ...t, columns: next };
    });

  const removeColumn = (key) =>
    setTable((t) => {
      // A table with no columns exports as an empty block, so the last one
      // stays. Removing the table itself is the "Remove table" button.
      if (t.columns.length <= 1) return t;
      return { ...t, columns: t.columns.filter((c) => c.key !== key) };
    });

  const moveColumn = (key, dir) =>
    setTable((t) => {
      const i = t.columns.findIndex((c) => c.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= t.columns.length) return t;
      const next = [...t.columns];
      [next[i], next[j]] = [next[j], next[i]];
      return { ...t, columns: next };
    });

  // ---- rows --------------------------------------------------------------
  // A row is a slot: its starting values live at that index in prefillRows, and
  // a legacy table's label lives at the same index in rowLabels. So every row
  // operation splices the two lists in step — which is what makes "insert a row
  // between these two" move the rows below it DOWN rather than shuffling their
  // contents. The row count itself is either the label list's length or
  // minRows, so exactly one of those two is what an insert/remove adjusts.
  //
  // A table's sub-headings (an eligibility checklist's "Medical Conditions:")
  // are keyed by the row they precede, which makes them a per-row slot exactly
  // like a label or a starting value. So they splice in step with the other two:
  // insert a row above "Medical Conditions:" and the heading moves down with the
  // criterion it introduces, instead of staying on a row number that now means
  // something else. This editor has no control for them — they ride through as
  // `table.print.groupRows`; see PRINT_TABLE_FIELDS in QuestionBuilder.
  const groupList = (t, n) => {
    const g = (t.print && t.print.groupRows) || {};
    return Array.from({ length: n }, (_, i) => g[i]);
  };
  const withGroups = (t, groups) => {
    if (!t.print || !t.print.groupRows) return t;
    const out = {};
    groups.forEach((label, i) => {
      if (label) out[i] = label;
    });
    return { ...t, print: { ...t.print, groupRows: out } };
  };

  const withRows = (t, fn) => {
    const count = tableRowCount(t);
    const labels = Array.isArray(t.rowLabels) ? [...t.rowLabels] : [];
    const prefill = padRows(t.prefillRows, count);
    const groups = groupList(t, count);
    const next = fn({ count, labels, prefill, groups });
    if (!next) return t;
    const base = labels.length
      ? { ...t, rowLabels: next.labels, prefillRows: next.prefill }
      : { ...t, minRows: next.prefill.length, prefillRows: next.prefill };
    return withGroups(base, groups);
  };

  const insertRow = (at) =>
    setTable((t) =>
      withRows(t, ({ count, labels, prefill, groups }) => {
        if (count >= MAX_TABLE_ROWS) return null;
        prefill.splice(at, 0, {});
        groups.splice(at, 0, undefined);
        if (labels.length) labels.splice(at, 0, "");
        return { labels, prefill };
      })
    );

  const removeRow = (at) =>
    setTable((t) =>
      withRows(t, ({ count, labels, prefill, groups }) => {
        // A table with no rows exports as headings over nothing; removing the
        // table itself is the "Remove table" button.
        if (count <= 1) return null;
        prefill.splice(at, 1);
        // A heading on the deleted row moves DOWN onto the row that takes its
        // place — it introduced the rows below it, and they are still there.
        const orphan = groups.splice(at, 1)[0];
        if (orphan && groups.length && !groups[at]) groups[at] = orphan;
        if (labels.length) labels.splice(at, 1);
        return { labels, prefill };
      })
    );

  const moveRow = (at, dir) =>
    setTable((t) =>
      withRows(t, ({ count, labels, prefill, groups }) => {
        const j = at + dir;
        if (j < 0 || j >= count) return null;
        [prefill[at], prefill[j]] = [prefill[j], prefill[at]];
        // The heading stays put: it introduces a POSITION in the list, not the
        // particular criterion that happens to sit there.
        if (labels.length) [labels[at], labels[j]] = [labels[j], labels[at]];
        return { labels, prefill };
      })
    );

  const setRowLabel = (at, v) =>
    setTable((t) => ({
      ...t,
      rowLabels: t.rowLabels.map((l, i) => (i === at ? v : l)),
    }));

  // Fold a legacy table's separate label column back into the table. The
  // labels are moved into the first ordinary column rather than thrown away —
  // that column is where a table's row names belong now.
  const foldRowLabelColumn = async () => {
    setMenu(null);
    const ok = await confirm(
      "The row names move into this table's first column, and the extra label column stops printing on the form.",
      { title: "Remove the row-label column?", confirmLabel: "Remove it" }
    );
    if (!ok) return;
    setTable((t) => {
      const labels = Array.isArray(t.rowLabels) ? t.rowLabels : [];
      const first = t.columns[0];
      const prefill = padRows(t.prefillRows, labels.length).map((row, i) =>
        // Only fill the first column where it's actually empty, so a value
        // already typed there is never overwritten by a label.
        first && labels[i] && !row[first.key]
          ? { ...row, [first.key]: labels[i] }
          : row
      );
      return {
        ...t,
        rowLabels: [],
        rowLabelHeader: "",
        minRows: labels.length,
        prefillRows: prefill,
      };
    });
  };

  // Pasting a list into a row label fills DOWN, the way a spreadsheet does —
  // this is what the old editor's "Paste a list" box did, moved to where the
  // rows actually are.
  const onRowLabelPaste = (at) => (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (!text.includes("\n")) return; // a normal one-line paste, leave it alone
    const lines = text
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (lines.length < 2) return;
    e.preventDefault();
    setTable((t) => {
      const labels = [...t.rowLabels];
      const count = tableRowCount(t);
      const prefill = padRows(t.prefillRows, count);
      const groups = groupList(t, count);
      // The row pasted into keeps its starting values; the rows added below it
      // are new and start empty.
      const added = lines.map((_, k) => (k === 0 ? prefill[at] : {}));
      labels.splice(at, 1, ...lines);
      prefill.splice(at, 1, ...added);
      // Splice the headings in step, as every other row operation does.
      groups.splice(at, 1, ...lines.map((_, k) => (k === 0 ? groups[at] : undefined)));
      return withGroups(
        {
          ...t,
          rowLabels: labels.slice(0, MAX_TABLE_ROWS),
          prefillRows: prefill.slice(0, MAX_TABLE_ROWS),
        },
        groups.slice(0, MAX_TABLE_ROWS)
      );
    });
  };

  const setPrefill = (at, colKey, val) =>
    setTable((t) => {
      const rows = padRows(t.prefillRows, tableRowCount(t));
      rows[at] = { ...rows[at], [colKey]: val };
      return { ...t, prefillRows: rows };
    });

  // Looked up by key each render rather than captured when the menu opened, so
  // edits made IN the menu show up in the grid behind it immediately. A column
  // that no longer exists simply renders no menu.
  const menuColumn =
    menu?.kind === "col" ? cols.find((c) => c.key === menu.key) : null;
  const menuColumnIndex = menuColumn ? cols.indexOf(menuColumn) : -1;

  const cellCls =
    "border border-white/15 align-top p-0 min-w-[9rem] bg-white/[0.02]";

  return (
    <div className="rounded-2xl border border-rose-400/25 bg-rose-500/[0.04] p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-rose-400/15">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-200">
          <Table2 className="w-3.5 h-3.5" /> Table
        </span>
        <span className="text-[11px] text-slate-400 tabular-nums">
          {rowCount} row{rowCount === 1 ? "" : "s"} × {cols.length} column
          {cols.length === 1 ? "" : "s"}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="ml-auto text-[11px] text-rose-300 hover:text-rose-200 transition"
        >
          Remove table, start over
        </button>
      </div>

      <p className="text-[11px] text-slate-500">
        Leave a cell empty for staff to write in, or type in it to have it print
        already filled in. For a dropdown or checklist column that pre-checks
        one of its choices, rather than replacing them.
      </p>

      {/* Only ever shown for a table built before this editor — see the file
          header. New tables have no separate label column to remove. */}
      {hasRowLabels && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-400/[0.07] px-2.5 py-2 flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-amber-100">
            This table has a separate row-label column, from before row names
            lived in the table itself. It prints as an extra first column.
          </span>
          <button
            type="button"
            onClick={foldRowLabelColumn}
            className="px-2 py-1 rounded-lg text-[11px] text-amber-100 bg-amber-400/15 border border-amber-300/30 hover:bg-amber-400/25 transition"
          >
            Move the names into the first column
          </button>
        </div>
      )}

      {/* The grid itself. group/grid is what makes every boundary "+" fade in
          while the pointer is anywhere over the table, rather than only over
          the one cell it belongs to. */}
      <div className="group/grid -mx-1 overflow-x-auto px-3 py-2.5">
        <table className="border-collapse">
          <thead>
            {/* Insert-a-column-here handles, sitting on each column's left
                boundary. Appending is the "+" at the far right instead. */}
            <tr>
              <th className="w-6 p-0" />
              {hasRowLabels && <th className="p-0" />}
              {cols.map((c, i) => (
                <th key={c.key} className="relative h-3 p-0">
                  <InsertHandle
                    onClick={() => insertColumn(i)}
                    title={`Insert a column before “${c.label || `Column ${i + 1}`}”`}
                    className="left-0 top-1/2 -translate-x-1/2 -translate-y-1/2"
                  />
                </th>
              ))}
              <th className="p-0" />
            </tr>

            <tr>
              <th className="w-6 p-0" />
              {hasRowLabels && (
                <th className={`${cellCls} bg-white/[0.06]`}>
                  <input
                    className="w-full bg-transparent px-2 py-1.5 text-[11px] font-semibold text-slate-200 outline-none placeholder:font-normal placeholder:text-slate-600"
                    value={table.rowLabelHeader || ""}
                    onChange={(e) =>
                      setTable((t) => ({ ...t, rowLabelHeader: e.target.value }))
                    }
                    placeholder="Row heading (e.g. Body System)"
                    title="Heading for the row-label column — optional"
                  />
                </th>
              )}
              {cols.map((c, i) => (
                <th key={c.key} className={`${cellCls} bg-white/[0.06] text-left`}>
                  <div className="flex items-start gap-1 pr-1">
                    <input
                      className="min-w-0 flex-1 bg-transparent px-2 pt-1.5 text-[11px] font-semibold text-slate-100 outline-none placeholder:font-normal placeholder:text-slate-600"
                      value={c.label}
                      onChange={(e) => updateColumn(c.key, { label: e.target.value })}
                      placeholder={`Column ${i + 1}`}
                    />
                    <button
                      type="button"
                      onClick={(ev) => openMenu({ kind: "col", key: c.key }, ev)}
                      title="Column type, choices and layout"
                      aria-label={`Options for column ${c.label || i + 1}`}
                      className="shrink-0 mt-1 w-5 h-5 rounded flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/15 transition"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {/* What this column actually holds, under its heading —
                      otherwise the type would be invisible until the menu is
                      opened, and a table's columns are mostly distinguished
                      by their type. */}
                  <div className="px-2 pb-1.5 text-[10px] font-normal normal-case text-slate-500 truncate">
                    {FIELD_TYPE_LABELS[c.type] || c.type}
                    {c.type === "Number" && c.unit ? ` · ${c.unit}` : ""}
                    {(c.type === "Select" || c.type === "MultiSelect") &&
                      ` · ${(c.options || []).filter(Boolean).length} choice${
                        (c.options || []).filter(Boolean).length === 1 ? "" : "s"
                      }`}
                  </div>
                </th>
              ))}
              {/* Add a column at the end. */}
              <th className="p-0 pl-1 align-middle">
                <button
                  type="button"
                  onClick={() => insertColumn(cols.length)}
                  disabled={cols.length >= MAX_TABLE_COLS}
                  title="Add a column at the end"
                  className="w-7 h-8 rounded-lg flex items-center justify-center text-emerald-200 bg-emerald-400/10 border border-emerald-400/25 hover:bg-emerald-400/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </th>
            </tr>
          </thead>

          <tbody>
            {Array.from({ length: rowCount }).map((_, ri) => (
              // Rows are positional here by nature: a fixed row is identified
              // by its slot in rowLabels, a blank one carries nothing at all.
              // eslint-disable-next-line react/no-array-index-key
              <tr key={ri} className="group/row">
                <td className="relative w-6 p-0 align-middle">
                  <InsertHandle
                    onClick={() => insertRow(ri)}
                    title={`Insert a row above row ${ri + 1}`}
                    className="left-1/2 top-0 -translate-x-1/2 -translate-y-1/2"
                  />
                  <button
                    type="button"
                    onClick={(ev) => openMenu({ kind: "row", index: ri }, ev)}
                    title={`Row ${ri + 1} — insert, move or delete`}
                    aria-label={`Options for row ${ri + 1}`}
                    className="w-5 h-6 mx-auto rounded flex items-center justify-center text-slate-500 opacity-40 group-hover/row:opacity-100 focus:opacity-100 hover:text-white hover:bg-white/10 transition"
                  >
                    <MoreVertical className="w-3.5 h-3.5" />
                  </button>
                </td>

                {hasRowLabels && (
                  <td className={cellCls}>
                    <input
                      ref={(el) => {
                        rowInputs.current[ri] = el;
                      }}
                      className="w-full bg-transparent px-2 py-1.5 text-xs text-slate-100 outline-none placeholder:text-slate-600"
                      value={rowLabels[ri] || ""}
                      onChange={(e) => setRowLabel(ri, e.target.value)}
                      onPaste={onRowLabelPaste(ri)}
                      onKeyDown={(e) => {
                        // Enter adds the next row and jumps into it, so a list
                        // of rows is typed straight through.
                        if (e.key === "Enter") {
                          e.preventDefault();
                          insertRow(ri + 1);
                          setFocusRow(ri + 1);
                        }
                      }}
                      placeholder={`Row ${ri + 1}`}
                    />
                  </td>
                )}

                {cols.map((c) => (
                  <td key={c.key} className={`${cellCls} px-1 py-1`}>
                    <DefaultValueInput
                      piece={c}
                      value={table.prefillRows?.[ri]?.[c.key]}
                      onChange={(v) => setPrefill(ri, c.key, v)}
                      placeholder="—"
                    />
                  </td>
                ))}
                <td className="p-0" />
              </tr>
            ))}
          </tbody>
        </table>

        {/* Add a row at the end. Sits under the grid the way Docs' add-row
            control does, and spans it so it's obviously about the whole table. */}
        <button
          type="button"
          onClick={() => insertRow(rowCount)}
          disabled={rowCount >= MAX_TABLE_ROWS}
          className="mt-1.5 ml-6 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] text-emerald-200 bg-emerald-400/10 border border-emerald-400/25 hover:bg-emerald-400/20 disabled:opacity-30 disabled:cursor-not-allowed transition"
        >
          <Plus className="w-3.5 h-3.5" /> Add a row
        </button>
      </div>

      {menu?.kind === "col" && menuColumn && (
        <AnchoredMenu rect={menu.rect} onClose={closeMenu}>
          <div className="p-2.5 space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">
              Column {menuColumnIndex + 1}
            </div>
            <label className="block">
              <span className="text-[11px] text-slate-500">Heading</span>
              <input
                className={`${inputCls} mt-0.5 !py-1.5 text-xs`}
                value={menuColumn.label}
                onChange={(e) => updateColumn(menuColumn.key, { label: e.target.value })}
                placeholder={`Column ${menuColumnIndex + 1}`}
              />
            </label>
            <label className="block">
              <span className="text-[11px] text-slate-500">What goes in this column</span>
              <select
                className={`${selectCls} mt-0.5 w-full !py-1.5 text-xs`}
                value={menuColumn.type}
                title={FIELD_TYPE_HINTS[menuColumn.type]}
                onChange={(e) => {
                  const type = e.target.value;
                  updateColumn(menuColumn.key, {
                    type,
                    options:
                      type === "Select" || type === "MultiSelect"
                        ? menuColumn.options || []
                        : undefined,
                    unit: type === "Number" ? menuColumn.unit : "",
                  });
                }}
              >
                {COLUMN_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-[#0b0820]">
                    {FIELD_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>
            {FIELD_TYPE_HINTS[menuColumn.type] && (
              <p className="text-[10px] text-slate-500">
                {FIELD_TYPE_HINTS[menuColumn.type]}
              </p>
            )}
            {menuColumn.type === "Number" && (
              <input
                className={`${inputCls} !py-1.5 text-xs`}
                value={menuColumn.unit || ""}
                onChange={(e) => updateColumn(menuColumn.key, { unit: e.target.value })}
                placeholder="Unit (e.g. kg, mmHg) — optional"
              />
            )}
            {(menuColumn.type === "Select" || menuColumn.type === "MultiSelect") && (
              <OptionsEditor
                type={menuColumn.type}
                options={menuColumn.options || []}
                onChange={(options) => updateColumn(menuColumn.key, { options })}
                confirm={confirm}
              />
            )}
            <div className="pt-2 border-t border-white/10 grid grid-cols-2 gap-1">
              <MenuAction
                icon={ArrowLeft}
                label="Insert left"
                onClick={() => {
                  insertColumn(menuColumnIndex);
                  closeMenu();
                }}
                disabled={cols.length >= MAX_TABLE_COLS}
              />
              <MenuAction
                icon={ArrowRight}
                label="Insert right"
                onClick={() => {
                  insertColumn(menuColumnIndex + 1);
                  closeMenu();
                }}
                disabled={cols.length >= MAX_TABLE_COLS}
              />
              <MenuAction
                icon={ArrowLeft}
                label="Move left"
                onClick={() => {
                  moveColumn(menuColumn.key, -1);
                  closeMenu();
                }}
                disabled={menuColumnIndex === 0}
              />
              <MenuAction
                icon={ArrowRight}
                label="Move right"
                onClick={() => {
                  moveColumn(menuColumn.key, 1);
                  closeMenu();
                }}
                disabled={menuColumnIndex === cols.length - 1}
              />
            </div>
            <MenuAction
              icon={Trash2}
              label="Delete column"
              danger
              onClick={() => {
                removeColumn(menuColumn.key);
                closeMenu();
              }}
              disabled={cols.length <= 1}
              title={
                cols.length <= 1
                  ? "A table needs at least one column — use “Remove table” instead"
                  : "Delete column"
              }
            />
          </div>
        </AnchoredMenu>
      )}

      {menu?.kind === "row" && (
        <AnchoredMenu rect={menu.rect} onClose={closeMenu} width={190}>
          <div className="p-1.5 space-y-0.5">
            <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-slate-400">
              Row {menu.index + 1}
            </div>
            <MenuAction
              icon={ArrowUp}
              label="Insert row above"
              onClick={() => {
                insertRow(menu.index);
                closeMenu();
              }}
              disabled={rowCount >= MAX_TABLE_ROWS}
            />
            <MenuAction
              icon={ArrowDown}
              label="Insert row below"
              onClick={() => {
                insertRow(menu.index + 1);
                closeMenu();
              }}
              disabled={rowCount >= MAX_TABLE_ROWS}
            />
            <MenuAction
              icon={ArrowUp}
              label="Move row up"
              onClick={() => {
                moveRow(menu.index, -1);
                closeMenu();
              }}
              disabled={menu.index === 0}
            />
            <MenuAction
              icon={ArrowDown}
              label="Move row down"
              onClick={() => {
                moveRow(menu.index, 1);
                closeMenu();
              }}
              disabled={menu.index === rowCount - 1}
            />
            <MenuAction
              icon={Trash2}
              label="Delete row"
              danger
              onClick={() => {
                removeRow(menu.index);
                closeMenu();
              }}
              disabled={rowCount <= 1}
              title={
                rowCount <= 1
                  ? "A table needs at least one row — use “Remove table” instead"
                  : "Delete row"
              }
            />
          </div>
        </AnchoredMenu>
      )}
    </div>
  );
}
